import { useReadCurrentIntradayBatchId, useReloadLatestBidSubmissions } from 'api/elplan/bidSubmissions.api'
import type { IntradayBatchIdReading, LatestIntradayBatchId } from 'api/elplan/elplan.types'
import { useState } from 'react'
import { useSubmissionBaseline } from './useSubmissionBaseline'

type NewerSubmissionWarningInput = {
  systemId: number | undefined
  bidBaseStartTime: Date
  hasBidInput: boolean
  submit: () => Promise<number | undefined>
}

function isNewerThanBaseline(currentBatchId: IntradayBatchIdReading, baselineBatchId: LatestIntradayBatchId): boolean {
  if (currentBatchId === 'unknown' || currentBatchId === null) return false
  return baselineBatchId === null || currentBatchId > baselineBatchId
}

export function useNewerSubmissionWarning({
  systemId,
  bidBaseStartTime,
  hasBidInput,
  submit,
}: NewerSubmissionWarningInput) {
  const { baselineBatchId, recordSubmittedBatch, retakeBaseline } = useSubmissionBaseline({
    systemId,
    bidBaseStartTime,
    hasBidInput,
  })
  const readCurrentBatchId = useReadCurrentIntradayBatchId()
  const reloadLatestBidSubmissions = useReloadLatestBidSubmissions()
  const [isSubmitPending, setIsSubmitPending] = useState(false)
  const [isWarningOpen, setIsWarningOpen] = useState(false)
  const [bidTableLoad, setBidTableLoad] = useState(0)

  async function hasNewerSubmission(): Promise<boolean> {
    if (systemId === undefined || baselineBatchId === 'unknown') return false
    return isNewerThanBaseline(await readCurrentBatchId(systemId), baselineBatchId)
  }

  async function submitAndRecord(): Promise<void> {
    const submittedSystemId = systemId
    const batchId = await submit()
    if (batchId !== undefined && submittedSystemId !== undefined) {
      recordSubmittedBatch(submittedSystemId, batchId)
    }
  }

  async function runOneAtATime(task: () => Promise<void>): Promise<void> {
    if (isSubmitPending) return
    setIsSubmitPending(true)
    try {
      await task()
    } finally {
      setIsSubmitPending(false)
    }
  }

  function submitOrWarn(): Promise<void> {
    return runOneAtATime(async () => {
      if (await hasNewerSubmission()) {
        setIsWarningOpen(true)
        return
      }
      await submitAndRecord()
    })
  }

  function confirmSubmitAnyway(): Promise<void> {
    setIsWarningOpen(false)
    return runOneAtATime(submitAndRecord)
  }

  function dismissWarning(): void {
    setIsWarningOpen(false)
  }

  async function loadLatest(): Promise<void> {
    setIsWarningOpen(false)
    if (systemId === undefined) return
    const reloadedSystemId = systemId
    setBidTableLoad((load) => load + 1)
    await reloadLatestBidSubmissions(reloadedSystemId)
    retakeBaseline(reloadedSystemId)
  }

  return {
    isSubmitPending,
    submitOrWarn,
    bidTableLoad,
    warningDialog: {
      open: isWarningOpen,
      onClose: dismissWarning,
      onConfirm: confirmSubmitAnyway,
      onLoadLatest: loadLatest,
    },
  }
}
